import { Project } from '@/shared/config/projects';
import { projectAxios } from '@/shared/core/axios';

import { Book, GetBooks, GetBooksParams } from '../model/types';
import { deleteBook, getAllBooks } from './books-api';

export type KitabaBookSummary = {
  id: number;
  author: { id: number; name: string };
  title: string;
  coverUrl: string;
  isPublished: boolean;
  categories?: { id: number; title: string }[];
};

export type KitabaBookPayload = {
  authorId?: number;
  title?: string;
  description?: string;
  file?: string;
  coverUrl?: string;
  translatorName?: string;
  weight?: number;
  isPublished?: boolean;
  categoriesIds?: number[];
  publisherId?: number;
};

export function mapKitabaSummary(book: KitabaBookSummary): Book {
  return {
    id: book.id,
    author: book.author.name,
    authorId: book.author.id,
    title: book.title,
    imgUrl: book.coverUrl,
    circleImgUrl: '',
    imagePath: '',
    circleImagePath: '',
    isPublished: book.isPublished,
    description: '',
    translatorName: '',
    readerName: '',
    categoryIds: (book.categories || []).map((category) => category.id),
    publisherId: null,
  };
}

export async function listAdminBooks(
  project: Project,
  params: GetBooksParams,
  signal?: AbortSignal,
): Promise<GetBooks> {
  if (project === 'hatfa') return getAllBooks(params, signal);

  const response = (
    await projectAxios.kitaba.get('/books', {
      params: {
        limit: params.limit,
        offset: params.offset,
        is_published: params.published,
        search: params.searchQuery,
      },
      signal,
    })
  ).data as { books: KitabaBookSummary[]; count: number };

  return {
    books: response.books.map(mapKitabaSummary),
    count: response.count,
  };
}

export async function deleteAdminBook(project: Project, id: number) {
  if (project === 'hatfa') return deleteBook(id);
  return projectAxios.kitaba.delete(`/books/${id}`);
}

export async function getKitabaBook(id: string): Promise<Book> {
  const book = (await projectAxios.kitaba.get(`/books/${id}`)).data as {
    id: number;
    author: { id: number; name: string };
    title: string;
    description?: string;
    file?: string;
    coverUrl: string;
    translatorName?: string;
    isPublished: boolean;
    categories?: { id: number; title: string }[];
    publisher?: { id: number; title: string };
  };

  return {
    id: book.id,
    author: book.author.name,
    authorId: book.author.id,
    title: book.title,
    imgUrl: book.coverUrl,
    circleImgUrl: '',
    imagePath: '',
    circleImagePath: '',
    isPublished: book.isPublished,
    description: book.description || '',
    translatorName: book.translatorName || '',
    readerName: '',
    categoryIds: (book.categories || []).map((category) => category.id),
    publisherId: book.publisher?.id ?? null,
    fileUrl: book.file || null,
    categoryTitles: (book.categories || []).map((category) => category.title),
    publisherTitle: book.publisher?.title ?? null,
  };
}

export async function createKitabaBook(payload: KitabaBookPayload) {
  await projectAxios.kitaba.post('/books', payload);
}

export async function updateKitabaBook(id: string, payload: KitabaBookPayload) {
  await projectAxios.kitaba.patch(`/books/${id}`, payload);
}
